"use client";

import { useState } from "react";
import { ArrowRight, Loader, Mail, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inquiryTypes = [{
  value: "roofing",
  label: "METAL ROOFING"
}, {
  value: "siding",
  label: "SIDING & CLADDING"
}, {
  value: "panels",
  label: "ACM / LUX PANELS"
}, {
  value: "stone",
  label: "STONE VENEER"
}, {
  value: "other",
  label: "OTHER"
}];
const directChannels = [{
  id: 1,
  label: "CALL US",
  value: "(587) 888-4223",
  icon: "phone"
}, {
  id: 2,
  label: "EMAIL",
  value: "info@metallineexteriors.ca",
  icon: "mail"
}, {
  id: 3,
  label: "WHATSAPP",
  value: "(587) 888-4223",
  icon: "message"
}];
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [messageLength, setMessageLength] = useState<number>(0);
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-card p-12 text-center rounded-lg shadow-md">
        <p className="font-default text-xs uppercase tracking-widest text-primary mb-4">
          REQUEST / RECEIVED
        </p>
        <p className="font-default text-3xl text-gray-900 mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default text-xs uppercase tracking-widest rounded-md">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-[10px] uppercase tracking-[0.2em] text-gray-500">
                FULL NAME *
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Your Name" className="w-full min-h-[48px] h-auto border-0 border-b border-gray-300 bg-transparent px-0 py-4 text-base text-gray-900 placeholder:text-gray-400 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-[10px] uppercase tracking-[0.2em] text-gray-500">
                EMAIL ADDRESS
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" inputMode="email" placeholder="Your Email" className="w-full min-h-[48px] h-auto border-0 border-b border-gray-300 bg-transparent px-0 py-4 text-base text-gray-900 placeholder:text-gray-400 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-[10px] uppercase tracking-[0.2em] text-gray-500">
                PHONE NUMBER *
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" inputMode="tel" placeholder="Your Phone Number" className="w-full min-h-[48px] h-auto border-0 border-b border-gray-300 bg-transparent px-0 py-4 text-base text-gray-900 placeholder:text-gray-400 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="postal_code" rules={{
        required: "Postal code is required"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-[10px] uppercase tracking-[0.2em] text-gray-500">
                POSTAL CODE *
              </FormLabel>
              <FormControl>
                <Input {...field} type="text" autoComplete="postal-code" placeholder="Your Postal Code" className="w-full min-h-[48px] h-auto border-0 border-b border-gray-300 bg-transparent px-0 py-4 text-base text-gray-900 placeholder:text-gray-400 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="space-y-3 col-span-1 md:col-span-2">
              <FormLabel className="font-default text-[10px] uppercase tracking-[0.2em] text-gray-500">
                SERVICE INTEREST *
              </FormLabel>
              <FormControl>
                <ToggleGroup type="single" value={field.value} onValueChange={v => {
            if (v) {
              field.onChange(v);
            }
          }} className="flex flex-wrap gap-2 w-full justify-start">
                  {inquiryTypes.map((type, i) => <ToggleGroupItem key={type.value} data-index={i} value={type.value} className="rounded-md border border-gray-300 bg-transparent text-gray-900 hover:bg-gray-100 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary px-5 py-2.5 font-default text-[10px] uppercase tracking-[0.2em] flex-none">
                      {type.label}
                    </ToggleGroupItem>)}
                </ToggleGroup>
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      validate: value => {
        if (!value || value.length === 0) return true;
        return value.length >= 10 || "Min 10 characters";
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 relative">
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.2em] text-gray-500">
              PROJECT DETAILS
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Textarea {...field} placeholder="Tell us about your renovation project..." rows={4} maxLength={1000} onChange={e => {
            field.onChange(e);
            setMessageLength(e.target.value.length);
          }} className="w-full min-h-[140px] border-0 border-b border-gray-300 bg-transparent px-0 py-4 text-base text-gray-900 placeholder:text-gray-400 focus-visible:border-primary focus-visible:ring-0 resize-none transition-colors" />
                <div className="absolute bottom-3 right-0 font-default text-[10px] uppercase tracking-widest text-gray-400">
                  {messageLength} / 1000
                </div>
              </div>
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <FormField control={control} name="acknowledged" render={({
      field
    }) => <FormItem className="space-y-1">
            <div className="flex items-start gap-3 pt-2">
              <FormControl>
                <Checkbox id="acknowledge" checked={!!field.value} onCheckedChange={checked => {
            field.onChange(checked === true);
          }} className="mt-0.5 border-gray-400 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
              </FormControl>
              <label htmlFor="acknowledge" className="font-default text-[11px] uppercase tracking-[0.15em] text-gray-600 cursor-pointer leading-relaxed">
                I agree to be contacted regarding my project inquiry.
              </label>
            </div>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <p className="text-xs text-gray-500 leading-relaxed">
        We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
        <Link to="/PrivacyPolicyPage" className="text-gray-900 underline underline-offset-2 hover:text-primary transition-colors">
          Privacy Policy
        </Link>
        .
      </p>

      {submitError && <div className="border border-red-500/50 bg-red-50 p-4 rounded-md">
          <p className="font-default text-xs uppercase tracking-widest text-red-700">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-gray-900 text-white hover:bg-gray-800 font-default text-xs uppercase tracking-[0.25em] group shadow-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            SENDING...
          </> : <>
            REQUEST QUOTE
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>

      <p className="font-default text-[10px] uppercase tracking-[0.25em] text-gray-500 text-center">
        TYPICAL RESPONSE / 24 HOURS · MONDAY–FRIDAY
      </p>
    </Form>;
}
export default function Consultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="2790" id="consultationform" className="relative bg-white text-gray-900 overflow-hidden ![padding:20px_32px_30px_32px]">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[60%] bg-gradient-to-t from-primary/5 via-gray-50 to-transparent blur-3xl" />
      </div>

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-[920px] mx-auto text-center mb-20 md:mb-28">
          <p className="font-default text-[11px] uppercase tracking-[0.3em] text-primary mb-8">
            GET A QUOTE
          </p>
          <h2 className="font-default font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-gray-900 leading-[0.95] mb-10 tracking-tight">
            START YOUR{" "}
            <span className="font-default italic font-normal text-gray-600">
              RENOVATION
            </span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Ready to upgrade your home? Our premium exterior specialists are here to provide expert guidance and a detailed project estimate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-10 gap-12 lg:gap-20 max-w-[1200px] mx-auto">
          <div className="lg:col-span-7">
            <FormProvider formKey="siding_consultation_form_siding" formId="siding_consultation_form_siding" sectionName="consultationform" defaultValues={{
            name: "",
            email: "",
            phone: "",
            postal_code: "",
            inquiry_type: "siding",
            message: "",
            acknowledged: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            phone: {
              required: true
            },
            postal_code: {
              required: true
            },
            email: {
              required: false,
              pattern: "email"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: false
            },
            acknowledged: {
              required: false
            }
          }} submitText="Request Quote" successMessage="Thank you! Your request has been submitted successfully. We will contact you shortly." errorMessage="Submission failed. Please try again or call us directly.">
              <InquiryFormContent />
            </FormProvider>
          </div>

          <aside className="lg:col-span-3 lg:pl-8 lg:border-l lg:border-gray-200">
            <div className="sticky top-24">
              <p className="font-default text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-8">
                CONTACT US
              </p>
              <div className="space-y-10">
                {directChannels.map((channel, i) => <div data-index={i} key={channel.id} className="group">
                    <div className="flex items-center gap-2 mb-3">
                      {channel.icon === "phone" && <Phone className="size-3 text-primary" />}
                      {channel.icon === "mail" && <Mail className="size-3 text-primary" />}
                      {channel.icon === "message" && <MessageCircle className="size-3 text-primary" />}
                      <p className="font-default text-[10px] uppercase tracking-[0.25em] text-gray-500">
                        {channel.label}
                      </p>
                    </div>
                    {channel.id === 3 ? <Link to="https://wa.me/15878884223" newTab className="font-default text-xl md:text-2xl font-semibold uppercase tracking-tight text-gray-900 hover:text-primary transition-colors cursor-pointer break-all block">
                        {channel.value}
                      </Link> : channel.id === 1 ? <Link to="tel:5878884223" className="font-default text-xl md:text-2xl font-semibold uppercase tracking-tight text-gray-900 hover:text-primary transition-colors cursor-pointer break-all block">
                        {channel.value}
                      </Link> : channel.id === 2 ? <Link to="mailto:info@metallineexteriors.ca" className="font-default text-base md:text-lg font-semibold uppercase tracking-tight text-gray-900 hover:text-primary transition-colors cursor-pointer break-all block">
                        {channel.value}
                      </Link> : <p className="font-default text-xl md:text-2xl font-semibold uppercase tracking-tight text-gray-900 hover:text-primary transition-colors cursor-pointer break-all">
                        {channel.value}
                      </p>}
                  </div>)}
              </div>

              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="font-default text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">
                  OFFICE HOURS
                </p>
                <p className="font-default text-xs text-gray-600 leading-relaxed">
                  Monday — Friday
                  <br />
                  08:00 — 17:00
                  <br />
                  Weekends / By Appointment
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}